import { baseApi } from "./baseQuery";

// Helper function to get workspace ID from localStorage (now ObjectId)
const getWorkspaceId = (): string => localStorage.getItem("workspace_id") ?? "Demo";

const integrationApi = baseApi.injectEndpoints({
  endpoints: (build) => ({

    // GOOGLE INTEGRATION
    integration: build.query({
      query: (body) => {
        // Get workspace ObjectId from localStorage
        const workspaceId = getWorkspaceId();
        return {
          url: `/api/v1/oauth?basepath=${body.basepath}&workspace_id=${workspaceId}`,
          method: "GET",
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${body.token}`,
          },
        }
      },
    }),

    verifyGoogleToken: build.query({
      query: (payload) => {
        const params = new URLSearchParams({
          code: payload.code,
          basepath: payload.basepath
        });

        // Add state parameter if provided
        if (payload.state) {
          params.append('state', payload.state);
        }

        return {
          url: `/api/v1/google/verify?${params.toString()}`,
          method: "GET",
        }
      },
    }),

    getAnalyticStatus: build.query({
      query: (workspace_id) => {
        const workspaceId = workspace_id || getWorkspaceId();
        return {
          url: `/api/v1/google/analytics-status?workspace_id=${workspaceId}`,
          method: "GET",
        }
      },
      providesTags: (result, error, arg) => [
        'metaIntegration',
        { type: 'integration-status', id: arg }, // Tag with workspace_id for cache invalidation
        { type: 'workspace-integration', id: arg } // Additional tag for workspace-specific data
      ],
      keepUnusedDataFor: 0 // Don't cache to ensure fresh data on workspace changes
    }),

    getAllToolsAccountsForReselect: build.query({
      query: ({ tool }) => {
        return {
          url: `/api/v1/tools-accounts?workspace_id=${getWorkspaceId()}&tool=${tool}`,
          method: "GET",
        }
      },
    }),

    removeGA: build.query({
      query: () => {
        return {
          url: `/api/v1/google/remove?workspace_id=${getWorkspaceId()}`,
          method: "GET",
        }
      },
    }),

    // NEW: Delete GA integration using REST API
    deleteGAIntegration: build.mutation({
      query: () => {
        return {
          url: `/api/v1/integrations/ga4/?workspace_id=${getWorkspaceId()}`,
          method: "DELETE",
        }
      },
      invalidatesTags: ['metaIntegration']
    }),

    getAnalyticsData: build.query({
      query: (payload) => {
        return {
          url: `/api/v1/fetch-data-from-bigquery?property_id=${payload?.property_id}&workspace_id=${getWorkspaceId()}`,
          method: "GET",
          // body: { property_id: payload?.property_id },
        }
      },
    }),
    downloadAnalyticsData: build.query({
      query: (payload) => {
        return {
          url: `/api/v1/ga-data-excel-download?property_id=${payload?.property_id}&workspace_id=${getWorkspaceId()}`,
          method: "GET",
          responseHandler: ((response) => response.blob()),
          // body: { property_id: payload?.property_id },
        }
      },
    }),

    // SHOPIFY INTEGRATION
    shopifyIntegration: build.query({
      query: (payload) => {
        return {
          url: `/api/v1/shopify/auth/start?shop=${payload?.data}&basepath=${payload?.basepath}&workspace_id=${getWorkspaceId()}`,
          method: "GET",
        }
      },
    }),

    shopifyDirectIntegration: build.query({
      query: (payload) => {
        return {
          url: `/api/v1/shopify/save-token?workspace_id=${getWorkspaceId()}&shop=${payload?.data?.shop}&access_token=${payload?.data?.access_token}`,
          method: "GET",
        }
      },
    }),

    verifyShopify: build.query({
      query: (body) => {
        return {
          url: `/api/v1/shopify/auth?code=${body.code}&hmac=${body.hmac}&host=YWRtaW4uc2hvcGlmeS5jb20vc3RvcmUvcXVpY2tzdGFydC05MDFmMmNjYg&shop=${body.shop}&state${body.state}&basePath=${body.basePath}&workspace_id=${getWorkspaceId()}`,
          method: "GET",
        }
      },
    }),
    removeShopify: build.query({
      query: () => {
        return {
          url: `/api/v1/shopify/remove?workspace_id=${getWorkspaceId()}`,
          method: "GET",
        }
      }
    }),

    // NEW: Delete Shopify integration using REST API
    deleteShopifyIntegration: build.mutation({
      query: () => {
        return {
          url: `/api/v1/integrations/shopify/?workspace_id=${getWorkspaceId()}`,
          method: "DELETE",
        }
      },
      invalidatesTags: ['metaIntegration']
    }),
    saveShopifyUserData: build.query({
      query: (body) => {
        return {
          url: `/api/v1/shopify/save-shopify-token-from-external`,
          method: "POST",
          body: {
            'shopify_email': body.shopify_email,
            'oogwai_email': body.oogwai_email,
            'shopify-token': body.token,
            'shop-domain': body.shopDomain,
            'workspace_id': getWorkspaceId()
          }
        }
      }
    }),
    getShopifyUserData: build.query({
      query: ({ email }) => {
        return {
          url: `/api/v1/shopify/get-user-data`,
          method: "POST",
          body: {
            'email': email
          }
        }
      }
    }),
    downloadShopifyData: build.query({
      query: () => {
        return {
          url: `/api/v1/shopify/download?workspace_id=${getWorkspaceId()}`,
          method: "GET",
          responseHandler: ((response) => response.blob()),
          // body: { property_id: payload?.property_id },
        }
      },
    }),
    getShopifyData: build.query({
      query: (payload) => {
        return {
          url: `/api/v1/shopify/data?table=${payload?.table_name}&page=${payload?.page}&limit=${payload?.limit}&workspace_id=${getWorkspaceId()}`,
          method: "GET",
          // headers: {
          //   'Content-Type': 'application/json',
          //   'Authorization': `Bearer ${payload?.token}`,
          // },
        }
      },
      keepUnusedDataFor: 0,
    }),
    getShopifyStats: build.query({
      query: (payload) => {
        return {
          url: `/api/v1/shopify/stats/?workspace_id=${getWorkspaceId()}&table=${payload?.table_name}`,
          method: "GET",
        }
      },
      keepUnusedDataFor: 0,
    }),

    // META INTEGRATION
    getMetaAccounts: build.query({
      query: () => {
        return {
          url: `/api/v1/meta/fetch/data?workspace_id=${getWorkspaceId()}`,
          method: "GET",
        }
      },
    }),
    metaIntegration: build.query({
      query: (payload) => {
        // Get workspace_id from localStorage
        const workspaceId = getWorkspaceId();
        return {
          url: `/api/v1/meta/auth/start?workspace_id=${workspaceId}&basepath=${payload.basepath}`,
          method: "GET",
        }
      },
    }),

    metaVerify: build.mutation({
      query: (payload) => {
        return {
          url: `/api/v1/meta/verify?code=${payload.code}&basepath=${payload.basepath}&workspace_id=${getWorkspaceId()}`,
          method: "POST",
        }
      },
      invalidatesTags: ['metaIntegration']
    }),

    removeMeta: build.query({
      query: () => {
        return {
          url: `/api/v1/meta/remove?workspace_id=${getWorkspaceId()}`,
          method: "GET",
        }
      }
    }),

    // NEW: Delete Meta integration using REST API
    deleteMetaIntegration: build.mutation({
      query: () => {
        return {
          url: `/api/v1/integrations/meta_ads/?workspace_id=${getWorkspaceId()}`,
          method: "DELETE",
        }
      },
      invalidatesTags: ['metaIntegration']
    }),

    getMetaAdAccounts: build.query({
      query: () => {
        return {
          url: `/api/v1/meta/get-ad-accounts?workspace_id=${getWorkspaceId()}`,
          method: "GET",
        }
      },
    }),

    getListMetaAccounts: build.query({
      query: () => {
        return {
          url: `/api/v1/meta/list/accounts?workspace_id=${getWorkspaceId()}`,
          method: "GET",
        }
      },
    }),
    getMetaData: build.query({
      query: ({ account_id }) => {
        return {
          url: `/api/v1/meta/fetch/user-data?workspace_id=${getWorkspaceId()}&account_id=${account_id}`,
          method: "GET",
          // headers: {
          //   'Content-Type': 'application/json',
          //   'Authorization': `Bearer ${payload?.token}`,
          // },
        }
      },
    }),
    downloadMetaData: build.query({
      query: ({ account_id }) => {
        return {
          url: `/api/v1/meta/download?workspace_id=${getWorkspaceId()}&account_id=${account_id}`,
          method: "GET",
          responseHandler: ((response) => response.blob()),
        }
      },
    }),

    // LIST ANALYTICS ACCOUNTS
    getListAnalyticsAccounts: build.query({
      query: () => {
        return {
          url: `/api/v1/list-property-id-dropdown?workspace_id=${getWorkspaceId()}`,
          method: "GET",
        }
      },
    }),
    // 
    getListAccountNames: build.query({
      query: () => {
        return {
          url: `/api/v1/list-account-name-dropdown?workspace_id=${getWorkspaceId()}`,
          method: "GET",
        }
      },
      providesTags: ['createProfile']
    }),

    getAccountNamesAfterSelection: build.query({
      query: (accountName) => {
        return {
          url: `/api/v1/list-property-name-dropdown-after-account-selection?account_name=${accountName}&workspace_id=${getWorkspaceId()}`,
          method: "GET",
        }
      },
    }),

    getCustomBigQueryConversationDropdown: build.query({
      query: (workspaceId) => {
        const workspace = workspaceId || getWorkspaceId();
        return {
          url: `/api/v1/custom-bigquery-conversation-dropdown?workspace_id=${workspace}`,
          method: "GET",
        }
      },
      providesTags: ['metaIntegration']
    }),

    // GOOGLE ADS
    googleAds: build.query({
      query: (body) => {
        // Get workspace ObjectId from body or fallback to Demo
        const workspaceId = body.workspace_object_id || "Demo";
        return {
          url: `/api/v1/googleAds/oauth?basepath=${body.basepath}&workspace_id=${workspaceId}`,
          method: "GET",
        }
      },
    }),
    verifyGoogleAdsToken: build.query({
      query: (payload) => {
        const params = new URLSearchParams({
          code: payload.code,
          basepath: payload.basepath
        });

        // Add state parameter if provided
        if (payload.state) {
          params.append('state', payload.state);
        }

        return {
          url: `/api/v1/googleAds/verify?${params.toString()}`,
          method: "GET",
        }
      },
    }),
    removeGoogleAds: build.query({
      query: () => {
        return {
          url: `/api/v1/googleAds/remove?workspace_id=${getWorkspaceId()}`,
          method: "GET",
        }
      }
    }),

    // NEW: Delete Google Ads integration using REST API
    deleteGoogleAdsIntegration: build.mutation({
      query: () => {
        return {
          url: `/api/v1/integrations/google_ads/?workspace_id=${getWorkspaceId()}`,
          method: "DELETE",
        }
      },
      invalidatesTags: ['metaIntegration']
    }),
    getGoogleAdsData: build.query({
      query: ({ customerId }) => {
        return {
          url: `/api/v1/google-ads/fetch/user-data?workspace_id=${getWorkspaceId()}&customer_id=${customerId}`,
          method: "GET",
        }
      }
    }),
    getGoogleAdsAccount: build.query({
      query: () => {
        return {
          url: `/api/v1/google-ads/accounts?workspace_id=${getWorkspaceId()}`,
          method: "GET",
        }
      }
    }),
    getFirstGoogleAdsData: build.query({
      query: () => {
        return {
          url: `/api/v1/google-ads/first/fetch?workspace_id=${getWorkspaceId()}`,
          method: "GET",
        }
      },
    }),
    getListGoogleAdsAccountNames: build.query({
      query: () => {
        return {
          url: `/api/v1/google-ads/list/accounts?workspace_id=${getWorkspaceId()}`,
          method: "GET",
        }
      },
      providesTags: ['createProfile']
    }),
    downloadGoogleAdsData: build.query({
      query: (payload) => {
        return {
          url: `/api/v1/google-ads/download?workspace_id=${getWorkspaceId()}&customer_id=${payload.customerId}`,
          method: "GET",
          responseHandler: ((response) => response.blob()),
        }
      },
    }),

    // YOUTUBE INTEGRATION
    youtubeIntegration: build.query({
      query: (body) => {
        return {
          url: `/api/v1/youtube/oauth?basepath=${body.basepath}`,
          method: "GET",
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${body.token}`,
          },
        }
      },
    }),

    verifyYoutubeToken: build.query({
      query: (payload) => {
        const params = new URLSearchParams({
          code: payload.code,
          basepath: payload.basepath,
          workspace_id: getWorkspaceId()
        });

        // Add state parameter if provided
        if (payload.state) {
          params.append('state', payload.state);
        }

        return {
          url: `/api/v1/youtube/verify?${params.toString()}`,
          method: "GET",
        }
      },
    }),

    getYoutubeData: build.query({
      query: () => {
        return {
          url: `/api/v1/youtube/data/Demo?workspace_id=${getWorkspaceId()}`,
          method: "GET",
        }
      },
      providesTags: ['metaIntegration']
    }),

    removeYoutube: build.query({
      query: () => {
        return {
          url: `/api/v1/youtube/remove?workspace_id=${getWorkspaceId()}`,
          method: "GET",
        }
      },
    }),

    // NEW: Delete YouTube integration using REST API
    deleteYoutubeIntegration: build.mutation({
      query: () => {
        return {
          url: `/api/v1/integrations/youtube/?workspace_id=${getWorkspaceId()}`,
          method: "DELETE",
        }
      },
      invalidatesTags: ['metaIntegration']
    }),

    listYoutube: build.query({
      query: () => {
        return {
          url: `/api/v1/youtube/fetch-channel?workspace_id=${getWorkspaceId()}`,
          method: "GET",
        }
      },
    }),

    // Select a YouTube channel for the workspace
    selectYoutubeChannel: build.mutation({
      query: ({ yt_channel_id }) => {
        return {
          url: `/api/v1/youtube/select-yt-channel-for-workspace?workspace_id=${getWorkspaceId()}`,
          method: "POST",
          body: { yt_channel_id }
        }
      },
      invalidatesTags: ['metaIntegration']
    }),

    // event data
    getEventData: build.query({
      query: () => {
        return {
          url: `/api/v1/get-custom-events_from_ga?workspace_id=${getWorkspaceId()}`,
          method: "GET",

        }
      },
    }),

    getEventDataDB: build.query({
      query: (payload) => {
        return {
          url: `/api/v1/get-custom-events_from_db?property_id=${payload?.property_id}&workspace_id=${getWorkspaceId()}`,
          method: "GET",
        }
      }
    }),

    updateEventDB: build.mutation({
      query: (payload) => {
        return {
          url: `/api/v1/update-custom-events?workspace_id=${getWorkspaceId()}`,
          method: "PUT",
          body: payload
        }
      }
    }),

    // PROFILEs
    getProfiles: build.query({
      query: () => {
        return {
          url: `/api/v1/workspaces`,
          method: "GET",
        }
      },
      providesTags: ['createProfile']
    }),

    deleteWorkspace: build.mutation({
      query: (profile_id) => {
        return {
          url: `/api/v1/delete-profile?workspace_id=${profile_id ?? "Demo"}`,
          method: "DELETE",
        }
      },
      invalidatesTags: ['createProfile']
    }),

    addGaAccount: build.mutation({
      query: (payload) => {
        return {
          url: `/api/v1/select-profile-for-workspace?workspace_id=${payload?.workspace_id ?? "Demo"}`,
          method: "POST",
          body: payload?.data
        }
      },
    }),

    integrationGaAccount: build.query({
      query: () => {
        console.log(getWorkspaceId(), 'workspace_id type')
        return {
          url: `/api/v1/list-analytics-accounts?workspace_id=${getWorkspaceId()}`,
          method: "GET",
        }
      },
    }),

    addGoogleadsAccount: build.mutation({
      query: (payload) => {
        return {
          url: `/api/v1/google-ads/select/accounts?workspace_id=${getWorkspaceId()}`,
          method: "POST",
          body: payload
        }
      },
    }),

    exportConversationTable: build.query({
      query: (value) => ({
        url: `/api/v1/download-excel-for-conversation-chat-table?chat_id=${value.chat_id}&timestamp=${value.timestamp}&workspace_id=${getWorkspaceId()}`,
        method: "GET",
        responseHandler: ((response) => response.blob())
      }),
      keepUnusedDataFor: 0,
    }),

    getQuestionQueryStatus: build.query({
      query: () => {
        return {
          url: `/api/v1/get-question-querying-status?workspace_id=${getWorkspaceId()}`,
          method: "GET",
        }
      },
    }),

    // BIGQUERY DATA INTEGRATION
    saveBigQueryProjectId: build.mutation({
      query: (payload) => {
        return {
          url: `/api/v1/save-custom-bigquery-project-id?workspace_id=${getWorkspaceId()}`,
          method: "POST",
          body: payload
        }
      },
    }),

    checkAccessInUserBigquery: build.mutation({
      query: (payload) => {
        return {
          url: `/api/v1/check-access-in-user-bigquery?workspace_id=${getWorkspaceId()}`,
          method: "POST",
          body: payload
        }
      },
    }),

    getBigQueryProjectIdStatus: build.query({
      query: () => {
        return {
          url: `/api/v1/get-bigquery-project-id-status?workspace_id=${getWorkspaceId()}`,
          method: "GET",
        }
      },
    }),

    getBigQueryDatasetDropdown: build.query({
      query: () => {
        return {
          url: `/api/v1/bigquery-dataset-dropdown?workspace_id=${getWorkspaceId()}`,
          method: "GET",
        }
      },
    }),

    listTablesInDataset: build.query({
      query: ({ integration_tool, dataset_id }) => {
        return {
          url: `/api/v1/list-tables-in-dataset?&workspace_id=${getWorkspaceId()}&dataset_id=${dataset_id}`,
          method: "GET",
        }
      },
      keepUnusedDataFor: 0
    }),

    selectDatasetForTool: build.mutation({
      query: ({ dataset_id, integration_tool }) => {
        return {
          url: `/api/v1/select-dataset-for-tool?dataset_id=${dataset_id}&integration_tool=${integration_tool}&workspace_id=${getWorkspaceId()}`,
          method: "POST",
        }
      },
    }),

    getTableSchema: build.query({
      query: ({ integration_tool, table_id }) => {
        return {
          url: `/api/v1/get-table-schema?integration_tool=${integration_tool}&table_id=${table_id}&workspace_id=${getWorkspaceId()}`,
          method: "GET",
        }
      },
    }),

    deleteCustomIntegrationDatasetId: build.mutation({
      query: ({ integration_tool }) => {
        return {
          url: `/api/v1/remove-dataset-id-for-each-tool?integration_tool=${integration_tool}&workspace_id=${getWorkspaceId()}`,
          method: "POST",
        }
      },
    }),

    updateBigQueryIntegrationDataset: build.mutation({
      query: (payload) => {
        return {
          url: `/api/v1/update-bigquery-description?workspace_id=${getWorkspaceId()}`,
          method: "POST",
          body: payload
        }
      },
    }),

    deleteMetaDatasetId: build.mutation({
      query: () => {
        return {
          url: `/api/v1/delete-meta-dataset-id?workspace_id=${getWorkspaceId()}`,
          method: "POST",
        }
      },
    }),

    getShopifyStatus: build.query({
      query: (email) => {
        return {
          url: `/api/v1/check-shopify-is_fetching-status?workspace_id=${getWorkspaceId()}&email=${email}`,
          method: "GET",
        }
      },
      keepUnusedDataFor: 0,
    }),

    saveTableNameUsingDataSet: build.mutation({
      query: (payload) => {
        return {
          url: `/api/v1/save-tables-and-descriptions?workspace_id=${getWorkspaceId()}&dataset_id=${payload.templateName}`,
          method: "POST",
          body: {
            'tables': payload.fields,
          }
        }
      },
      invalidatesTags: ['metaIntegration']
    }),

    // 1/fetch_schema_description_from_llm?workspace_id=seeaash \
    postSchemaDes: build.mutation({
      query: () => {
        return {
          url: `/api/v1/fetch_schema_description_from_llm?workspace_id=${getWorkspaceId()}`,
          method: "POST",
        }
      },
    }),

    getTableNameAfterViewSchema: build.query({
      query: () => {
        return {
          url: `/api/v1/get-table-names-from-db?workspace_id=${getWorkspaceId()}`,
          method: "GET",
        }
      },
    }),

    getTableSchemaFromDb: build.query({
      query: ({ tableName }) => {
        return {
          url: `/api/v1/get-table-schema-from-db?workspace_id=${getWorkspaceId()}&table_name=${tableName}`,
          method: "GET",
        }
      },
    }),


    updateTableSchemaDesc: build.mutation({
      query: (payload) => {
        return {
          url: `/api/v1/update-table-schema-descriptions?workspace_id=${getWorkspaceId()}`,
          method: "POST",
          body: payload
        }
      },
    }),

    checkSchemaDesc: build.query({
      query: () => {
        return {
          url: `/api/v1/get-schema-desc-fetched-status?workspace_id=${getWorkspaceId()}`,
          method: "GET",
        }
      },
      keepUnusedDataFor: 0
    }),

    removeCustomBigQuery: build.mutation({
      query: () => {
        return {
          url: `/api/v1/remove-custom-bigquery-integration?workspace_id=${getWorkspaceId()}`,
          method: "POST",
        }
      },
      invalidatesTags: ['metaIntegration']
    }),

    // NEW: Delete Custom BigQuery integration using REST API
    deleteCustomBigQueryIntegration: build.mutation({
      query: () => {
        return {
          url: `/api/v1/integrations/custom_bigquery/?workspace_id=${getWorkspaceId()}`,
          method: "DELETE",
        }
      },
      invalidatesTags: ['metaIntegration']
    }),

    getCustomName: build.query({
      query: () => {
        return {
          url: `/api/v1/get-dataset-ui-name?workspace_id=${getWorkspaceId()}`,
          method: "GET",
        }
      },
      providesTags: ['customIntegration', 'metaIntegration']
    }),

    editCustomName: build.mutation({
      query: (payload) => {
        return {
          url: `/api/v1/change-dataset-ui-name?workspace_id=${getWorkspaceId()}`,
          method: "POST",
          body: payload
        }
      },
      invalidatesTags: ['customIntegration']
    }),

    getDataSourceList: build.query({
      query: ({ search = '', workspaceId }: { search?: string; workspaceId?: string }) => {
        return {
          url: `/api/v1/list-property-id-dropdown?workspace_id=${workspaceId || getWorkspaceId()}&query=${search}`,
          method: "GET",
        }
      },
      providesTags: ['createProfile']
    }),

    // NEW INTEGRATION MANAGEMENT ENDPOINTS
    getIntegrationList: build.query({
      query: () => {
        return {
          url: `/api/v1/integrations/?workspace_id=${getWorkspaceId()}`,
          method: "GET",
        }
      },
      providesTags: ['integrationList']
    }),

    getIntegrationStatus: build.query({
      query: (workspace_id) => {
        const workspaceId = workspace_id || getWorkspaceId();
        return {
          url: `/api/v1/integrations/status/?workspace_id=${workspaceId}`,
          method: "GET",
        }
      },
      providesTags: ['integrationStatus']
    }),

    getIntegrationErrors: build.query({
      query: () => {
        return {
          url: `/api/v1/integrations/errors?workspace_id=${getWorkspaceId()}`,
          method: "GET",
        }
      },
      providesTags: ['integrationErrors']
    }),

  }),

  // @ts-ignore
  overrideExisting: module.hot?.status() === "apply",
});

export const {
  useLazyIntegrationQuery,
  useLazyVerifyGoogleTokenQuery,
  useGetAnalyticStatusQuery,
  useGetAllToolsAccountsForReselectQuery,
  useLazyRemoveGAQuery,
  useDeleteGAIntegrationMutation,
  useGetAnalyticsDataQuery,
  useLazyDownloadAnalyticsDataQuery,
  // SHOPIFY
  useLazyShopifyIntegrationQuery,
  useLazyShopifyDirectIntegrationQuery,
  useLazyVerifyShopifyQuery,
  useGetShopifyDataQuery,
  useGetShopifyStatsQuery,
  useLazyRemoveShopifyQuery,
  useDeleteShopifyIntegrationMutation,
  useLazySaveShopifyUserDataQuery,
  useGetShopifyUserDataQuery,
  useLazyDownloadShopifyDataQuery,
  useGetListMetaAccountsQuery,
  // META
  useLazyGetMetaAccountsQuery,
  useLazyMetaIntegrationQuery,
  useMetaVerifyMutation,
  useGetMetaDataQuery,
  useLazyRemoveMetaQuery,
  useDeleteMetaIntegrationMutation,
  useGetMetaAccountsQuery,
  useLazyGetMetaAdAccountsQuery,
  useLazyDownloadMetaDataQuery,
  // get property id
  useGetListAnalyticsAccountsQuery,
  useGetListAccountNamesQuery,
  useLazyGetAccountNamesAfterSelectionQuery,
  useGetCustomBigQueryConversationDropdownQuery,
  // GOOGLE ADS
  useLazyGoogleAdsQuery,
  useLazyVerifyGoogleAdsTokenQuery,
  useLazyRemoveGoogleAdsQuery,
  useDeleteGoogleAdsIntegrationMutation,
  useGetGoogleAdsDataQuery,
  useLazyGetGoogleAdsAccountQuery,
  useGetListGoogleAdsAccountNamesQuery,
  useLazyGetFirstGoogleAdsDataQuery,
  useLazyDownloadGoogleAdsDataQuery,
  // YOUTUBE
  useLazyYoutubeIntegrationQuery,
  useLazyVerifyYoutubeTokenQuery,
  useLazyRemoveYoutubeQuery,
  useDeleteYoutubeIntegrationMutation,
  useGetYoutubeDataQuery,
  useLazyListYoutubeQuery,
  useSelectYoutubeChannelMutation,
  // EVENT DATA
  useLazyGetEventDataQuery,
  useLazyGetEventDataDBQuery,
  useUpdateEventDBMutation,
  useDeleteWorkspaceMutation,
  useAddGaAccountMutation,
  useLazyIntegrationGaAccountQuery,
  useAddGoogleadsAccountMutation,

  useExportConversationTableQuery,
  useGetQuestionQueryStatusQuery,

  // BIGQUERY DATA INTEGRATION
  useSaveBigQueryProjectIdMutation,
  useCheckAccessInUserBigqueryMutation,
  useGetBigQueryProjectIdStatusQuery,
  useGetBigQueryDatasetDropdownQuery,
  useListTablesInDatasetQuery,
  useSelectDatasetForToolMutation,
  useGetTableSchemaQuery,
  // useDeleteShopifyDatasetIdMutation,
  // useDeleteMagentoDatasetIdMutation,
  useDeleteMetaDatasetIdMutation,
  useGetShopifyStatusQuery,
  useDeleteCustomIntegrationDatasetIdMutation,
  useUpdateBigQueryIntegrationDatasetMutation,

  useSaveTableNameUsingDataSetMutation,
  usePostSchemaDesMutation,
  useGetTableNameAfterViewSchemaQuery,
  useGetTableSchemaFromDbQuery,
  useUpdateTableSchemaDescMutation,
  useCheckSchemaDescQuery,
  useRemoveCustomBigQueryMutation,
  useDeleteCustomBigQueryIntegrationMutation,
  useGetCustomNameQuery,
  useEditCustomNameMutation,
  useGetDataSourceListQuery,

  // NEW INTEGRATION MANAGEMENT
  useGetIntegrationListQuery,
  useGetIntegrationStatusQuery,
  useGetIntegrationErrorsQuery,

} = integrationApi;
